import { invalidate } from '@react-three/fiber';
import { t } from 'i18next';

import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import {
    readChunkedJsonCookie,
    writeChunkedJsonCookie
} from 'app/modules/wallforming/components/cookies/cookieStorage';
import {
    cloneArticleInstance,
    mapArticleInstanceToPlacement
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { removeCachedInstanceEdges } from 'app/modules/wallforming/components/scenes/objects/articles/utils/edgesOverlayUtils';
import {
    scheduleBatchDelete,
    scheduleBatchPlace
} from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleSlice';
import {
    DEFAULT_HISTORY_SETTINGS,
    type HistoryState
} from 'app/modules/wallforming/components/state-management/placement/states/historyState';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { TranslationKey } from 'app/modules/wallforming/components/input-binding/types/translationKey';
import type { HistoryItem } from 'app/modules/wallforming/components/placement-history/types/historyItem';
import type { HistorySettings } from 'app/modules/wallforming/components/placement-history/types/historySettings';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StoreApi } from 'zustand';


export const HISTORY_LIMIT = DEFAULT_HISTORY_SETTINGS.undoSteps;
export const HISTORY_BYTE_LIMIT = DEFAULT_HISTORY_SETTINGS.memoryLimitMb * 1024 * 1024;

export function normalizeHistorySettings(settings: Partial<HistorySettings>): HistorySettings {
    const bounded = (value: number | undefined, fallback: number, min: number, max: number) =>
        typeof value === 'number' && Number.isFinite(value)
            ? Math.max(min, Math.min(max, Math.floor(value)))
            : fallback;
    return {
        undoSteps: bounded(settings.undoSteps, DEFAULT_HISTORY_SETTINGS.undoSteps, 0, 1000),
        memoryLimitMb: bounded(settings.memoryLimitMb, DEFAULT_HISTORY_SETTINGS.memoryLimitMb, 1, 1024),
        repeatSteps: bounded(settings.repeatSteps, DEFAULT_HISTORY_SETTINGS.repeatSteps, 0, 1000)
    };
}

interface Change<T> {
    key: string;
    before: T | undefined;
    after: T | undefined;
}

interface InstanceChange extends Change<ArticleInstance> {
    articleId: string;
}

interface PoiChange {
    index: number;
    memberships: { articleId: string; added: string[]; removed: string[] }[];
    before: boolean;
    after: boolean;
}

interface HistoryEntry extends HistoryItem {
    instances: InstanceChange[];
    groups: Change<PlacementState['articleGroups'][string]>[];
    pois: PoiChange[];
    solutions: Change<string | null>[];
    beforeSelection: PlacementState['articleInstanceSelections'];
    afterSelection: PlacementState['articleInstanceSelections'];
    beforePoiSelection: PlacementState['poiSelection']['current'];
    afterPoiSelection: PlacementState['poiSelection']['current'];
    bytes: number;
}

function poiSelectionChanged(before: HistoryEntry['beforePoiSelection'], after: HistoryEntry['afterPoiSelection']) {
    return (
        before.index !== after.index ||
        before.filterType !== after.filterType ||
        (after.index !== undefined && before.shouldRealignCamera !== after.shouldRealignCamera)
    );
}

function hasChanges(entry: HistoryEntry): boolean {
    return (
        entry.instances.length > 0 ||
        entry.groups.length > 0 ||
        entry.pois.length > 0 ||
        entry.solutions.length > 0 ||
        poiSelectionChanged(entry.beforePoiSelection, entry.afterPoiSelection)
    );
}

// Only inspect changed maps. Immutable document references are retained for the duration
// of a transaction, never as history entries (which would retain the entire scene).
function changes<T>(
    before: Record<string, T>,
    after: Record<string, T>,
    clone: (value: T) => T,
    equal = (a: T | undefined, b: T | undefined) => JSON.stringify(a) === JSON.stringify(b)
): Change<T>[] {
    if (before === after) {
        return [];
    }
    const result: Change<T>[] = [];
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
        if (before[key] === after[key]) {
            continue;
        }
        if (equal(before[key], after[key])) {
            continue;
        }
        result.push({
            key,
            before: before[key] === undefined ? undefined : clone(before[key]),
            after: after[key] === undefined ? undefined : clone(after[key])
        });
    }
    return result;
}

function equalInstances(a: ArticleInstance | undefined, b: ArticleInstance | undefined): boolean {
    if (!a || !b) {
        return a === b;
    }
    if (
        (a.isLocked ?? false) !== (b.isLocked ?? false) ||
        a.sourceType !== b.sourceType ||
        (a.sourceGroupId ?? null) !== (b.sourceGroupId ?? null) ||
        (a.sourceGroupName ?? null) !== (b.sourceGroupName ?? null) ||
        (a.sourceGroupInstanceId ?? null) !== (b.sourceGroupInstanceId ?? null) ||
        (a.sourceSavedGroupPrefabId ?? null) !== (b.sourceSavedGroupPrefabId ?? null)
    ) {
        return false;
    }
    const poiGuids = new Set(a.poiGuids ?? []);
    if (poiGuids.size !== (b.poiGuids?.length ?? 0) || b.poiGuids?.some((id) => !poiGuids.has(id))) {
        return false;
    }
    const names = Object.keys(a.parts);
    return (
        names.length === Object.keys(b.parts).length &&
        names.every((name) => {
            const first = a.parts[name];
            const second = b.parts[name];
            return (
                second &&
                first.rotation === second.rotation &&
                first.position.equals(second.position) &&
                first.alignment.equals(second.alignment) &&
                first.scale.equals(second.scale)
            );
        })
    );
}

function createEntry(
    before: PlacementState,
    after: PlacementState,
    label: TranslationKey,
    details?: HistoryItem['details']
): HistoryEntry {
    const instances: InstanceChange[] = [];
    if (before.articles !== after.articles) {
        for (const articleId of new Set([...Object.keys(before.articles), ...Object.keys(after.articles)])) {
            for (const change of changes(
                before.articles[articleId]?.articleInstances ?? {},
                after.articles[articleId]?.articleInstances ?? {},
                cloneArticleInstance,
                equalInstances
            )) {
                instances.push({ ...change, articleId });
            }
        }
    }
    const pois: PoiChange[] = [];
    if (before.pois !== after.pois) {
        after.pois.forEach((poi, index) => {
            const previous = before.pois[index];
            if (
                previous &&
                (previous.articles !== poi.articles || previous.autoFormworkPlaced !== poi.autoFormworkPlaced)
            ) {
                const memberships: PoiChange['memberships'] = [];
                for (const articleId of new Set([...Object.keys(previous.articles), ...Object.keys(poi.articles)])) {
                    if (previous.articles[articleId] === poi.articles[articleId]) {
                        continue;
                    }
                    const oldIds = new Set(previous.articles[articleId] ?? []);
                    const newIds = new Set(poi.articles[articleId] ?? []);
                    const added = [...newIds].filter((id) => !oldIds.has(id));
                    const removed = [...oldIds].filter((id) => !newIds.has(id));
                    if (added.length || removed.length) {
                        memberships.push({ articleId, added, removed });
                    }
                }
                if (memberships.length || previous.autoFormworkPlaced !== poi.autoFormworkPlaced) {
                    pois.push({
                        index,
                        memberships,
                        before: previous.autoFormworkPlaced,
                        after: poi.autoFormworkPlaced
                    });
                }
            }
        });
    }
    const entry: HistoryEntry = {
        id: 0,
        label:
            label === 'historyPoiSelection'
                ? after.poiSelection.current.index === undefined
                    ? 'historyPoiDeselection'
                    : after.poiSelection.current.shouldRealignCamera
                      ? 'historyPoiSelectionAligned'
                      : 'historyPoiSelectionUnaligned'
                : label,
        details,
        instances,
        groups: changes(before.articleGroups, after.articleGroups, (value) => structuredClone(value)),
        pois,
        solutions: changes(
            before.appliedCornerPlacementSolutionByPoi,
            after.appliedCornerPlacementSolutionByPoi,
            (value) => value
        ).filter((change) => change.before != null || change.after != null),
        beforeSelection: {
            ...(before.isAutoDragActiveRef.current ? before.lastSelectionCache : before.articleInstanceSelections)
        },
        afterSelection: {
            ...(after.isAutoDragActiveRef.current ? after.lastSelectionCache : after.articleInstanceSelections)
        },
        beforePoiSelection: { ...before.poiSelection.current },
        afterPoiSelection: { ...after.poiSelection.current },
        bytes: 0
    };
    entry.bytes =
        (JSON.stringify(entry).length +
            JSON.stringify(Object.keys(entry.beforeSelection)).length +
            JSON.stringify(Object.keys(entry.afterSelection)).length) *
        2;
    return entry;
}

function applyChanges<T>(
    source: Record<string, T>,
    delta: Change<T>[],
    direction: 'before' | 'after',
    clone: (value: T) => T = (value) => value
): Record<string, T> {
    if (delta.length === 0) {
        return source;
    }
    const result = { ...source };
    for (const change of delta) {
        const value = change[direction];
        if (value === undefined) {
            delete result[change.key];
        } else {
            result[change.key] = clone(value);
        }
    }
    return result;
}

function replay(api: StoreApi<PlacementState>, entry: HistoryEntry, direction: 'before' | 'after') {
    const state = api.getState();
    const articles = entry.instances.length ? { ...state.articles } : state.articles;
    const affectedArticles = new Set(entry.instances.map(({ articleId }) => articleId));
    for (const articleId of affectedArticles) {
        const article = articles[articleId];
        if (!article) {
            throw new Error(`Cannot restore missing article ${articleId}.`);
        }
        articles[articleId] = { ...article, articleInstances: { ...article.articleInstances } };
    }
    const removed: string[] = [];
    const restored = new Map<string, string[]>();
    for (const change of entry.instances) {
        const target = change[direction];
        if (target) {
            articles[change.articleId].articleInstances[change.key] = cloneArticleInstance(target);
            const ids = restored.get(change.articleId) ?? [];
            ids.push(change.key);
            restored.set(change.articleId, ids);
        } else {
            delete articles[change.articleId].articleInstances[change.key];
            removed.push(change.key);
        }
    }
    const articleGroups = applyChanges(state.articleGroups, entry.groups, direction, (value) => structuredClone(value));
    const instanceToGroupMap = entry.groups.length ? { ...state.instanceToGroupMap } : state.instanceToGroupMap;
    for (const change of entry.groups) {
        const previous = state.articleGroups[change.key];
        for (const member of previous?.members ?? []) {
            delete instanceToGroupMap[member.instanceId];
        }
    }
    for (const change of entry.groups) {
        for (const member of articleGroups[change.key]?.members ?? []) {
            instanceToGroupMap[member.instanceId] = change.key;
        }
    }
    const pois = entry.pois.length ? [...state.pois] : state.pois;
    for (const change of entry.pois) {
        const poi = pois[change.index];
        if (!poi) {
            throw new Error(`Cannot restore missing POI ${change.index}.`);
        }
        const memberships = { ...poi.articles };
        for (const membership of change.memberships) {
            const added = direction === 'after' ? membership.added : membership.removed;
            const removed = new Set(direction === 'after' ? membership.removed : membership.added);
            const ids = new Set((memberships[membership.articleId] ?? []).filter((id) => !removed.has(id)));
            added.forEach((id) => ids.add(id));
            if (ids.size) {
                memberships[membership.articleId] = [...ids];
            } else {
                delete memberships[membership.articleId];
            }
        }
        pois[change.index] = { ...poi, articles: memberships, autoFormworkPlaced: change[direction] };
    }
    const changedIds = entry.instances.map(({ key }) => key);
    const hiddenInstanceIds = changedIds.length ? new Set(state.hiddenInstanceIds) : state.hiddenInstanceIds;
    changedIds.forEach((id) => hiddenInstanceIds.delete(id));
    if (changedIds.length) {
        removeCachedInstanceEdges(changedIds);
        state.removeCollidersByRelations(changedIds);
        state.removeBatchInstances(removed);
        state.removeNavigatorBatchInstances(removed);
    }
    const poiSelection = direction === 'before' ? entry.beforePoiSelection : entry.afterPoiSelection;
    const restorePoi = poiSelectionChanged(state.poiSelection.current, poiSelection);
    if (restorePoi) {
        state.clearManualPlacementData();
        state.setArePivotControlsActive(false);
        state.setIsPanelQuickReplacementOpen(false);
        state.setFaceSelectionRef(null);
    }

    api.setState({
        articles,
        pois,
        articleGroups,
        instanceToGroupMap,
        appliedCornerPlacementSolutionByPoi: applyChanges(
            state.appliedCornerPlacementSolutionByPoi,
            entry.solutions,
            direction
        ),
        articleInstanceSelections: { ...(direction === 'before' ? entry.beforeSelection : entry.afterSelection) },
        ...(restorePoi
            ? {
                  poiSelection: { current: { ...poiSelection } },
                  isPoiSelectionFeedbackVisible: false
              }
            : {}),
        hiddenInstanceIds,
        placementUpdate: removed.length
            ? { event: PlacementUpdateEvent.DELETE, articleInstanceIds: new Set(removed) }
            : null
    });

    // The existing article lifecycle subscribers update both canvases, detached
    // drag previews, GPU batches, matrices and colliders from this atomic commit.
    if (removed.length) {
        scheduleBatchDelete(removed, api.getState);
    }
    if (restored.size) {
        scheduleBatchPlace(
            [...restored].map(([articleId, ids]) => ({
                articleGuid: articleId,
                articleNumber: articles[articleId].articleNumber,
                lifeCycleCode: articles[articleId].lifeCycleCode,
                representationGuid: EMPTY_GUID,
                instances: ids.map((id) =>
                    mapArticleInstanceToPlacement(
                        id,
                        articles[articleId].articleInstances[id],
                        articles[articleId].articleInstances[id].poiGuids ?? [],
                        !state.articles[articleId]?.articleInstances[id]
                    )
                )
            })),
            api.getState
        );
    }
    if (changedIds.length || entry.groups.length) {
        api.getState().toggleRecomputeTransformationBox();
    }
    if (changedIds.length || entry.groups.length || restorePoi) {
        api.getState().updateArticleEdgesOverlay();
    }
    invalidate();
}

export function createPlacementHistory(api: StoreApi<PlacementState>): HistoryState {
    const savedSettings = readChunkedJsonCookie('wallforming-history-settings');
    const settings = normalizeHistorySettings(savedSettings && typeof savedSettings === 'object' ? savedSettings : {});
    let undo: HistoryEntry[] = [];
    let redo: HistoryEntry[] = [];
    let repeats: (HistoryItem & { operation: () => void })[] = [];
    let nextId = 1;
    let baseId = 0;
    let transaction: {
        before: PlacementState;
        label: TranslationKey;
        repeat?: () => void;
        details?: HistoryItem['details'];
    } | null = null;
    let replaying = false;
    let commandDepth = 0;
    let commitRequested = false;
    const publish = () =>
        api.setState({
            canUndo: undo.length > 0,
            canRedo: redo.length > 0,
            undoLabel: undo[undo.length - 1]?.label ?? null,
            redoLabel: redo[redo.length - 1]?.label ?? null,
            undoHistory: [...undo, ...[...redo].reverse()].map(({ id, label, details }) => ({ id, label, details })),
            historyBaseId: baseId,
            historyPosition: undo[undo.length - 1]?.id ?? baseId,
            historyBytes: [...undo, ...redo].reduce((sum, entry) => sum + entry.bytes, 0),
            repeatHistory: repeats.map(({ id, label, details }) => ({ id, label, details })),
            isHistoryTransactionActive: transaction !== null
        });
    const prune = (settings: HistorySettings) => {
        let bytes = [...undo, ...redo].reduce((sum, entry) => sum + entry.bytes, 0);
        while (undo.length + redo.length > settings.undoSteps || bytes > settings.memoryLimitMb * 1024 * 1024) {
            let removed: HistoryEntry;
            if (undo.length) {
                removed = undo.shift()!;
                baseId = removed.id;
            } else {
                removed = redo.shift()!;
            }
            bytes -= removed.bytes;
        }
        repeats = settings.repeatSteps === 0 ? [] : repeats.slice(-settings.repeatSteps);
    };
    const clearHistory = () => {
        undo = [];
        redo = [];
        repeats = [];
        baseId = nextId++;
        transaction = null;
        commitRequested = false;
        publish();
    };
    let previewDepth = 0;
    const beginHistoryTransaction = (label: TranslationKey) => {
        if (transaction || replaying || previewDepth > 0) {
            return;
        }
        transaction = { before: api.getState(), label };
        publish();
    };
    const commitHistoryTransaction = () => {
        if (!transaction || replaying) {
            return;
        }
        if (commandDepth > 0) {
            commitRequested = true;
            return;
        }
        commitRequested = false;
        const { before, label, repeat, details } = transaction;
        transaction = null;
        const entry = createEntry(before, api.getState(), label, details);
        if (hasChanges(entry)) {
            entry.id = nextId++;
            redo = [];
            undo.push(entry);
            if (repeat) {
                repeats.push({ id: entry.id, label, details, operation: repeat });
            }
            prune(api.getState().historySettings);
        }
        publish();
    };
    const cancelHistoryTransaction = () => {
        if (!transaction || replaying) {
            return;
        }
        const { before, label } = transaction;
        transaction = null;
        commitRequested = false;
        const entry = createEntry(before, api.getState(), label);
        replaying = true;
        try {
            if (hasChanges(entry)) {
                replay(api, entry, 'before');
            }
        } finally {
            replaying = false;
            publish();
        }
    };
    const isBlocked = () => {
        const state = api.getState();
        return (
            transaction ||
            replaying ||
            state.isViewMode ||
            state.isAutoDragActiveRef.current ||
            state.isDraggingActive() ||
            state.isDragNewArticleStartRef.current
        );
    };
    const travel = (direction: 'before' | 'after') => {
        if (isBlocked()) {
            return false;
        }
        const source = direction === 'before' ? undo : redo;
        const entry = source[source.length - 1];
        if (!entry) {
            return false;
        }
        replaying = true;
        try {
            replay(api, entry, direction);
            source.pop();
            (direction === 'before' ? redo : undo).push(entry);
        } finally {
            replaying = false;
            publish();
        }
        return true;
    };
    api.subscribe((next, previous) => {
        if (next.sessionId !== previous.sessionId) {
            clearHistory();
        }
    });
    return {
        historySettings: settings,
        setHistorySettings: (settings) => {
            const normalized = normalizeHistorySettings(settings);
            api.setState({ historySettings: normalized });
            prune(normalized);
            publish();
            if (!writeChunkedJsonCookie('wallforming-history-settings', normalized)) {
                toastNotifications.warning({ title: t('warning'), message: t('warningHistorySettingsNotSaved') });
            }
        },
        undoHistory: [],
        historyBaseId: baseId,
        historyPosition: baseId,
        historyBytes: 0,
        repeatHistory: [],
        historyDialog: null,
        setHistoryDialog: (historyDialog) => api.setState({ historyDialog }),
        setHistoryRepeat: (label, operation) => {
            if (transaction?.label === label && !replaying && previewDepth === 0) {
                transaction.repeat = operation;
            }
        },
        setHistoryTransformDetail: (detail) => {
            if (transaction && !replaying && previewDepth === 0) {
                (transaction.details ??= []).push(structuredClone(detail));
            }
        },
        scrollToHistory: (id) => {
            if (isBlocked()) {
                return false;
            }
            const entries = [...undo, ...[...redo].reverse()];
            const target = id === baseId ? 0 : entries.findIndex((entry) => entry.id === id) + 1;
            if (id !== baseId && target === 0) {
                throw new Error(`Unknown history entry ${id}.`);
            }
            while (undo.length !== target) {
                if (!travel(undo.length > target ? 'before' : 'after')) {
                    return false;
                }
            }
            return true;
        },
        repeatLast: () => {
            const last = repeats[repeats.length - 1];
            return last ? api.getState().repeatHistoryItem(last.id) : false;
        },
        repeatHistoryItem: (id) => {
            if (isBlocked()) {
                return false;
            }
            const command = repeats.find((entry) => entry.id === id);
            if (!command) {
                throw new Error(`Unknown repeat entry ${id}.`);
            }
            const revision = nextId;
            api.getState().runHistoryTransaction(command.label, () => {
                command.operation();
                api.getState().setHistoryRepeat(command.label, command.operation);
            });
            if (nextId === revision) {
                toastNotifications.information({ title: t('info'), message: t('infoRepeatUnavailable') });
            }
            return nextId !== revision;
        },
        canUndo: false,
        canRedo: false,
        undoLabel: null,
        redoLabel: null,
        isHistoryTransactionActive: false,
        beginHistoryTransaction,
        commitHistoryTransaction,
        cancelHistoryTransaction,
        runWithoutHistory: (operation) => {
            previewDepth++;
            try {
                return operation();
            } finally {
                previewDepth--;
            }
        },
        runHistoryTransaction: (label, operation) => {
            if (previewDepth > 0) {
                return operation();
            }
            const ownsTransaction = !transaction && !replaying;
            if (ownsTransaction) {
                beginHistoryTransaction(label);
            }
            commandDepth++;
            let succeeded = false;
            try {
                const result = operation();
                succeeded = true;
                return result;
            } finally {
                commandDepth--;
                if (ownsTransaction && !succeeded) {
                    cancelHistoryTransaction();
                } else if (commandDepth === 0 && (ownsTransaction || commitRequested)) {
                    commitHistoryTransaction();
                }
            }
        },
        clearHistory,
        undo: () => travel('before'),
        redo: () => travel('after')
    };
}

export function bindHistoryActions(state: PlacementState): void {
    const wrap =
        <A extends unknown[], R>(label: TranslationKey, action: (...args: A) => R) =>
        (...args: A): R =>
            state.runHistoryTransaction(label, () => action(...args));
    const repeatable =
        <A extends unknown[], R>(label: TranslationKey, action: (...args: A) => R) =>
        (...args: A): R =>
            state.runHistoryTransaction(label, () => {
                const result = action(...args);
                state.setHistoryRepeat(label, () => action(...args));
                return result;
            });
    state.addPlacementIndicator = wrap('historyPlace', state.addPlacementIndicator);
    const addArticleInstance = state.addArticleInstance;
    state.addArticleInstance = (...args) =>
        args[2] && state.isAutoDragActiveRef.current
            ? state.runWithoutHistory(() => addArticleInstance(...args))
            : args[2]
              ? addArticleInstance(...args)
              : state.runHistoryTransaction('historyPlace', () => addArticleInstance(...args));
    state.addOrUpdateArticleInstance = wrap('historyEdit', state.addOrUpdateArticleInstance);
    state.updateArticleInstance = wrap('historyEdit', state.updateArticleInstance);
    state.updateArticleInstances = wrap('historyEdit', state.updateArticleInstances);
    state.replaceArticleInstance = wrap('historyReplace', state.replaceArticleInstance);
    state.removeAllArticleInstances = wrap('historyDelete', state.removeAllArticleInstances);
    state.removeArticleInstances = wrap('historyDelete', state.removeArticleInstances);
    const discardSelection = state.discardArticleInstanceSelection;
    state.discardArticleInstanceSelection = (...args) =>
        state.isAutoDragActiveRef.current
            ? state.runWithoutHistory(() => discardSelection(...args))
            : args[0]
              ? repeatable('historyDelete', discardSelection)(...args)
              : wrap('historyDelete', discardSelection)(...args);
    state.cutArticleInstances = repeatable('historyCut', state.cutArticleInstances);
    const rotate = state.handleOnArticleDefaultRotation;
    state.handleOnArticleDefaultRotation = (...args) =>
        state.isAutoDragActiveRef.current
            ? state.runWithoutHistory(() => rotate(...args))
            : repeatable('historyTransform', rotate)(...args);
    state.applyTranslationValue = repeatable('historyTransform', state.applyTranslationValue);
    state.applyRotationValue = wrap('historyTransform', state.applyRotationValue);
    state.applyScalingValue = repeatable('historyTransform', state.applyScalingValue);
    state.createArticleGroup = wrap('historyGroup', state.createArticleGroup);
    state.removeArticleGroup = wrap('historyGroup', state.removeArticleGroup);
    state.renameArticleGroup = wrap('historyGroup', state.renameArticleGroup);
    state.setArticleGroupColor = wrap('historyGroup', state.setArticleGroupColor);
    state.updateArticleGroup = wrap('historyGroup', state.updateArticleGroup);
    state.replaceInstanceInGroups = wrap('historyGroup', state.replaceInstanceInGroups);
    state.removeInstancesFromGroups = wrap('historyGroup', state.removeInstancesFromGroups);
    state.handleOnUngroupSelection = repeatable('historyGroup', state.handleOnUngroupSelection);

    const drag = state.handleOnDragArticle;
    state.handleOnDragArticle = (...args) =>
        state.isAutoDragActiveRef.current ? state.runWithoutHistory(() => drag(...args)) : drag(...args);
    const endDrag = state.handleOnDragArticleEnd;
    state.handleOnDragArticleEnd = (...args) => {
        if (state.isAutoDragActiveRef.current) {
            return state.runWithoutHistory(() => endDrag(...args));
        }
        state.beginHistoryTransaction('historyMove');
        try {
            return endDrag(...args);
        } finally {
            if (args[0]) {
                state.cancelHistoryTransaction();
            } else {
                state.commitHistoryTransaction();
            }
        }
    };
    const endTransform = state.handleOnArticleTransformationEnd;
    state.handleOnArticleTransformationEnd = (...args) => {
        try {
            return endTransform(...args);
        } finally {
            state.commitHistoryTransaction();
        }
    };
    const resetArticles = state.setArticles;
    state.setArticles = (articles) => {
        state.clearHistory();
        resetArticles(articles);
    };
    const resetPois = state.setPois;
    state.setPois = (pois) => {
        state.clearHistory();
        resetPois(pois);
    };
}
